import React, { FC, useEffect, useState } from 'react';
import { Loader, Sidebar } from 'components/common';
import {
  SidebarComponentProps,
  useSidebarManager,
} from 'contexts/SidebarManagerContext';
import {
  deleteExpenses,
  GetDetailsCategory,
} from 'features/expenses/services/Expenses';
import { formatDate } from 'utils/globalMethodes';
import { toast } from 'react-toastify';
import { useDialog } from 'contexts/DialogContext';
import NewExpense from './NewExpense';
import UserShortCut from 'components/common/UserShortCut';
import { ExpenseDetail, NoteStatus } from 'features/expenses/types/type';
import { useMediaContext } from 'contexts/MediaContext';
import Tippy from '@tippyjs/react';
import CategoryExpense from './CategoryExpense';
import { useTranslation } from 'react-i18next';

interface CategoryDetailsProps {
  catId: number;
  noteId: number;
  expenseId: number;
  toggleRefresh: () => void;
  toggleRefreshDT?: () => void;
}

const CategoryDetails: FC<SideBarProps<CategoryDetailsProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const { openSidebar, closeSidebar, closeMultipleSidebars } =
    useSidebarManager();
  const [expense, setExpense] = useState<ExpenseDetail>();
  const dialog = useDialog();
  const { openMedia } = useMediaContext();

  const fetchExpenseDetails = async (expenseId: number) => {
    const response = await GetDetailsCategory(expenseId);
    if (response.success) {
      setExpense(response.data);
    }
  };
  const handleDelete = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!params) return;
    const confirmed = await dialog.delete(
      'Expenses.Delete.Title',
      'Expenses.Delete.desc',
    );

    if (confirmed) {
      const response = await deleteExpenses(params.expenseId);
      if (response.success) {
        params.toggleRefresh();
        params?.toggleRefreshDT?.();
        closeSidebar(CategoryDetails);
        toast.success(t('Default.succesMessage'));
      } else {
        toast.error(response.message);
      }
    }
  };
  useEffect(() => {
    if (params.expenseId) {
      fetchExpenseDetails(params.expenseId);
    }
  }, [params.expenseId]);

  const handleOpenSidebar = () => {
    closeMultipleSidebars([
      CategoryDetails as React.FC<SidebarComponentProps>,
      NewExpense as React.FC<SidebarComponentProps>,
      CategoryExpense as React.FC<SidebarComponentProps>,
    ]);
    openSidebar(NewExpense, false, {
      ...params,
    });
  };

  function validatePdfOrImg(fileName: string): boolean {
    const ext = fileName.split('.').pop() as string;
    const extImg = ['png', 'jpg', 'jpeg', 'jfif'];
    const extPdf = ['pdf'];
    if (extImg.includes(ext)) {
      return true;
    } else if (extPdf.includes(ext)) {
      return true;
    } else {
      return false;
    }
  }
  const handelOpenFile = (
    fileName: string,
    title: string,
    path: string,
  ): void => {
    const ext = fileName.split('.').pop() as string;
    openMedia(`/upload/expenseDoc/${path}`, ext, title);
  };

  const renderAttachmentItem = (
    item: FileType,
    idx: number,
  ): React.JSX.Element => {
    const filename = item.name?.split('.');
    const ext = filename?.[filename.length - 1];
    let classe = '';
    let icon = 'eye.svg';
    let btn_text = 'Visualiser';
    if (item.mimetype == 'image') {
      classe = 'vue-image';
    } else if (ext == 'pdf') {
      classe = 'vue-pdf';
    } else {
      icon = 'download.svg';
      btn_text = 'download';
    }
    return (
      <div key={idx} className="col-sm-4 minthr-px-1 minthr-mb-2">
        <div className="minthr-box minthr-box-shadow minthr-box-round minthr-p-3 minthr-pb-2">
          <div className="minthr-flex minthr-mb-2">
            <div className="minthr-icon-bg minthr-icon-bg-l minthr-bg-secondary minthr-icon-rounded">
              <img
                className="minthr-icon-m minthr-mobile-icon-s"
                src="/icons/accounting.svg"
                alt=""
              />
            </div>
            <a
              className="minthr-ml-auto"
              onClick={() => {
                if (validatePdfOrImg(item.name ?? '')) {
                  handelOpenFile(
                    item.name ?? '',
                    item.name ?? '',
                    item.path ?? '',
                  );
                } else {
                  const downloadUrl = `/upload/DownloadFile/${item.id}`;
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = item.name ?? '';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
            >
              <img
                className={`minthr-icon-m minthr-icon-toxic ${classe}`}
                src={`/icons/${icon}`}
                alt=""
              />
            </a>
          </div>
          <Tippy content={item.name}>
            <div className="h5 minthr-border-b minthr-pb-1 minthr-mb-3 minthr-text-truncate">
              {item.name}
            </div>
          </Tippy>
          <div className="minthr-flex inthr-flex-items-center minthr-flex-space-between ">
            <a
              className={`minthr-icon-text minthr-color-toxic minthr-sidebar-open ${classe}`}
              onClick={() => {
                if (validatePdfOrImg(item.name ?? '')) {
                  handelOpenFile(
                    item.name ?? '',
                    item.name ?? '',
                    item.path ?? '',
                  );
                }
              }}
            >
              {t(btn_text)}
            </a>
          </div>
        </div>
      </div>
    );
  };

  if (!expense) return <Loader />;

  return (
    <Sidebar
      size="m"
      type="secondary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-3 minthr-border-b minthr-border-b-3">
        <h3>{t('Expense.details')}</h3>
        <button
          className="minthr-sidebar-close"
          type="button"
          onClick={() => closeSidebar(CategoryDetails)}
        >
          <img src="/icons/close.svg" alt="close" />
        </button>
      </div>

      <div className="minthr-pt-2">
        <div className="row g-0 minthr-mb-3">
          <div className="col-sm-12">
            <div className="minthr-flex minthr-flex-items-center">
              <div className="w-100">
                <div className="minthr-flex minthr-flex-items-start">
                  <div
                    className="minthr-icon-bg minthr-icon-bg-l minthr-icon-rounded minthr-mr-3"
                    style={{ backgroundColor: expense.expensesCat?.bgColor }}
                  >
                    <i
                      className={expense.expensesCat?.imgPath}
                      style={{ color: expense.expensesCat?.imgColor }}
                    ></i>
                  </div>
                  <div>
                    <h4>{expense.expensesCat?.name}</h4>
                    <div className="h4 minthr-color-primary">
                      {(expense.total ?? expense.amount)
                        .toFixed(2)
                        .replace('.', ',')}{' '}
                      {expense.currency}
                    </div>
                  </div>
                  {(expense.noteStatus == NoteStatus.Pending ||
                    expense.noteStatus == NoteStatus.Draft ||
                    expense.noteStatus == NoteStatus.Canceled) && (
                    <>
                      <button
                        type="button"
                        className="minthr-btn minthr-btn-icon-origin minthr-ml-auto"
                        onClick={handleOpenSidebar}
                      >
                        <img src="/icons/edit.svg" alt="edit" />
                      </button>
                      <button
                        type="button"
                        className="minthr-btn minthr-pl-0"
                        onClick={handleDelete}
                      >
                        <img
                          className="minthr-icon-danger"
                          src="/icons/delete.svg"
                          alt="delete"
                        />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-0 minthr-mb-2">
          <div className="col-sm-4">
            <span className="minthr-text-uppercase p">
              {t('Expense.StartDate')}
            </span>
          </div>
          <div className="col-sm-8">
            <div className="minthr-color-dark h5">
              {formatDate(new Date(expense.date), 'dd-MMM-yyyy', ' ')}
            </div>
          </div>
        </div>

        {expense.distance != null && (
          <div className="row g-0 minthr-mb-2">
            <div className="col-sm-4">
              <span className="minthr-text-uppercase p">
                {t('Expense.Distance')}
              </span>
            </div>
            <div className="col-sm-8">
              <div className="minthr-color-dark h5">{expense.distance} KM</div>
            </div>
          </div>
        )}

        {expense.supplier && (
          <div className="row g-0 minthr-mb-2">
            <div className="col-sm-4">
              <span className="minthr-text-uppercase p">
                {t('Expense.Supplier')}
              </span>
            </div>
            <div className="col-sm-8">
              <div className="minthr-color-dark h5">{expense.supplier}</div>
            </div>
          </div>
        )}

        <div className="row g-0 minthr-mb-2">
          <div className="col-sm-4">
            <span className="minthr-text-uppercase p">
              {t('Note.Affectedto')}
            </span>
          </div>
          <div className="col-sm-8">
            <div className="minthr-flex minthr-flex-items-center minthr-color-dark h5">
              {expense.userImage.fullName}
              <UserShortCut
                src={`upload/profileimages/${expense?.userImage.id}/${expense?.userImage.picturePath}`}
                alt={expense.userImage.fullName ?? ''}
                className="minthr-avatar minthr-avatar-s minthr-ml-2"
              />
            </div>
          </div>
        </div>

        <div className="row g-0 minthr-mb-2">
          <div className="col-sm-4">
            <span className="minthr-text-uppercase p">
              {t('Expense.Total')}
            </span>
          </div>
          <div className="col-sm-8">
            <div className="minthr-badge minthr-bg-light-gray minthr-mr-6 minthr-py-1">
              {expense.total?.toFixed(2).replace('.', ',') ??
                expense.amount?.toFixed(2).replace('.', ',')}{' '}
              {expense.currency}
            </div>
          </div>
        </div>

        {expense.payedBy && expense.payedBy !== 'none' && (
          <div className="row g-0 minthr-mb-2">
            <div className="col-sm-4">
              <span className="minthr-text-uppercase p">
                {t('Expense.PayedBy')}
              </span>
            </div>
            <div className="col-sm-8">
              <div className="minthr-color-dark h5">{t(expense.payedBy)}</div>
            </div>
          </div>
        )}

        {expense.description && (
          <div className="row g-0 minthr-flex-items-center minthr-mb-2">
            <div className="col-sm-12 minthr-mb-2">
              <span className="minthr-text-uppercase p">
                {t('Expense.description')}
              </span>
            </div>
            <div className="col-sm-12">
              <div className="minthr-color-dark h5">{expense.description}</div>
            </div>
          </div>
        )}
        {expense.filesNames.length > 0 && (
          <div className="row g-0 minthr-flex-items-center minthr-mb-1 minthr-border-t minthr-pt-3">
            <div className="col-sm-12 minthr-mb-2">
              <span className="minthr-text-uppercase p">
                {t('Expense.attachement')}
              </span>
            </div>
            <div className="row g-0 minthr-mb-5">
              <div className="row g-0 row-cols-sm-3 minthr-mt-3 minthr-mb-6">
                {expense.filesNames.map(renderAttachmentItem)}
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default CategoryDetails;
